<template>
    <div class="wt-container">
        <p v-if="!assessment.crop" class="introduction-text">
            Please select a crop or a crop category.
        </p>
        <p v-else-if="assessment.crop.cropID > 10000" class="introduction-text">
            This function does not work at the sector level. Please select a crop or a crop category.
        </p>
        <p v-else-if="assessment.crop.cropID == 1009" class="introduction-text">
            This function does not work for fodder crops. Please select another crop or crop category.
        </p>
        <div v-else>
            <p class="introduction-text">
                This table is similar to the table under "Sustainability", but it only shows the crop-country-basin combinations that are not efficient and/or not sustainable: the priority components of the {{assessment.viewColor}} water footprint of {{assessment.crop.name.toLowerCase()}} in {{assessment.getCountryOrBasinName(true)}}. Every priority component contains an appropriate response strategy.
            </p>
            <Table :columns="columns" :rows="rows" @open="openRow($event)">
                <template v-slot:rowdetail="slotProps">
                    <div class="row">
                        <div class="col-sm-3">
                          <div v-if="(assessment.viewColor == 'blue') && basin">
                            <p class="font-weight-bold">Monthly Blue Water Availability and Footprint in the {{basin.name}} Basin</p>
                            <HydroChart id="hydrograph"
                                        :structure="hydroData"
                                        :tooltipHeight="260"
                                        :tooltipWidth="200"
                                        :hasAxisLeft="false"
                                        :minHeight="500"
                                        :tooltipPosition="'bottom'"
                                        :minHeightType="'px'"></HydroChart>
                          </div>
                        </div>
                        <div class="col-sm-6">
                            <p class="detail-header">{{crop.name}} from {{getBasinName(slotProps.row.basinID)}} in {{country.name}}</p>
                            <div v-html="getRespondText(slotProps.row)"></div>
                        </div>
                        <div class="col-sm-3">
                          <div v-if="(assessment.viewColor == 'blue')? slotProps.row.blueWSSpot: slotProps.row.greyWSSpot">
                            <p class="font-weight-bold">Sectors and Crops in the {{basin.name}} Basin</p>
                            <PieChart v-if="pieData.length > 1"
                                      :id="'detail-pie-chart'" :color="assessment.viewColor"
                                      :data="pieData"
                                      :attrName="attributeName"
                                      :minHeight="300"
                                      :radius="70"
                                      :labelWidth="100"
                                      :labelDistance="30"
                                      :longLabel="false"
                                      :minHeightType="'px'">
                            </PieChart>
                            <BarChart
                                    :id="'detail-bar-chart'"
                                    :minHeight="170"
                                    :minHeightType="'px'"
                                    :data="barData"
                                    :labelAttr="'cropName'"
                                    :valueAttr="'percent'"
                            ></BarChart>
                            <p class="font-weight-bold">Deviations from the Benchmark in {{country.name}}</p>
                          </div>
                        </div>
                    </div>
                </template>
            </Table>
            <transition name="fade">
                <div v-if="countryBasinCropsLoader.status.loading" class="lottie">
                    <Lottie></Lottie>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import HydroChart from '@/components/shared/Hydrograph.vue'
import Table from "@/components/shared/Table.vue";
import Lottie from "@/components/shared/Lottie.vue";
import PieChart from "@/components/shared/PieChart.vue";
import BarChart from "@/components/shared/BarChart";
import { assessment } from "@/services/assessment-model.js";
import { EntityLoader, countryBasinCrops, countryBasinCropsLoader, getCountry, getBasin, getCrop } from "@/services/entity-model.js";
import { getFunctionGetCheckImage, getFunctionGetPercentageString, getFunctionGetWFString, getWFtString, getWSString } from "@/services/data-tools.js";
import * as _ from "lodash";

export default {
    name: "GeographicRespondTableCt",
    components: {
        HydroChart,
        PieChart,
        BarChart,
        Table,
        Lottie
    },
    data() {
        return {
            assessment: assessment,
            countryBasinCrops: countryBasinCrops,
            countryBasinCropsLoader: countryBasinCropsLoader,
            countryCrops: [],
            basinCrops: [],
            country: null,
            basin: null,
            crop: null
        }
    },
    mounted() {
        this.webURL = "https://waterfootprint.org/en/water-footprint/glossary/";
        this.threshold = 0.05;
        
        this.countryCropsLoader = new EntityLoader(this.countryCrops, "countryCrops"),
        this.countryCropsLoader.getUrl = function(countryId) {
            return `https://www.waterfootprintassessmenttool.org/nessie/entities/country-crops.jsp?format=json&cors=true&country=${countryId}`;
        }
        this.basinCropsLoader = new EntityLoader(this.basinCrops, "basinCrops"),
        this.basinCropsLoader.getUrl = function(basinId) {
            return `https://www.waterfootprintassessmenttool.org/nessie/entities/basin-crops.jsp?format=json&cors=true&basin=${basinId}`;
        }
    },
    methods: {
        openRow: function(params) {
            this.countryCropsLoader.load(params.countryID);
            this.basinCropsLoader.load(params.basinID);
            this.country = getCountry(params.countryID);
            this.basin = getBasin(params.basinID);
            this.crop = getCrop(params.cropID);
        },
        getBasinName: function(val) {
            return !val ? "Unknown River Basins" : "the " + this.basin.name + " Basin";
        },
        getRespondText: function(row) {
            const blue = this.assessment.viewColor === "blue";
            const wftSpot =  blue ? row.greenBlueWFtSpot : row.greyWFtSpot;
            const wft = blue ? getWFtString(row.greenBlueWFt) : getWFtString(row.greyWFt);
            const wft20 = blue ? getWFtString(row.greenBlueWFt20) : getWFtString(row.greyWFt20);
            const wftExcess = blue ? row.greenBlueWFtExcess : row.greyWFtExcess;
            const wsSpot = blue ? row.blueWSSpot : row.greyWSSpot;
            const ws = blue ? row.blueWS : row.greyWS;
            const frac = blue ? row.blueWFFraction2 : row.greyWFFraction2;
            const clrName0 = blue ? "green-blue" : "grey";
            const clrName1 = blue ? "blue" : "grey";
            const clrName2 = blue ? "<a href=\"" + this.webURL + "#BWS\" target=\"gloss\">blue water scarcity</a>" :
                                    "<a href=\"" + this.webURL + "#WPL\" target=\"gloss\">water pollution level</a>";
            const countryName = this.country.name;
            const basinName = this.getBasinName(row.basinID);
            const cropName = this.crop.name;

            let text = "";

            if (wftSpot) {
                text += "<p class='font-weight-bold'><img src='/cross.png' width='13' height='13'/> Not Efficient</p>";
                text += "<p>The " + clrName0 + " water footprint of " + cropName.toLowerCase() + " from " + countryName + " is " + wft + " and exceeds the <a href=\"" + this.webURL + "#WFB\" target=\"gloss\">global benchmark</a> by " + Math.round(wftExcess * 100) + "%. ";
                text += "The global " + clrName0 + " water footprint benchmark for " + cropName.toLowerCase() + " is " + wft20 + ".<br/>";
                text += "Response strategy: reduce water footprint through improved practices and technologies.</p>";
                text += "<p> </p>";
            } else if (wftExcess != null) {
                text += "<p class='font-weight-bold'><img src='/check.png' width='13' height='13'/> Efficient</p>";
                text += "<p>The " + clrName0 + " water footprint of " + cropName.toLowerCase() + " from " + countryName + " is within the <a href=\"" + this.webURL + "#WFB\" target=\"gloss\">global benchmark</a>.</p>";
                text += "<p> </p>";
            }

            if (wsSpot) {
                text += "<p class='font-weight-bold'><img src='/cross.png' width='13' height='13'/> Not Sustainable</p>";
                text += "<p>The annual average " + clrName2 + " in " + basinName + " is " + getWSString(ws) + ". ";
                if (blue) {
                    text += "The hydrograph on the left shows the monthly variety in the blue water scarcity.";
                }
                text += "<br/>";
                text += "Response strategy: reduce the basin water footprint through collective action.</p>";
                text += "<p> </p>";

                text += "<p>";
                if (cropName != null) {
                    if (frac >= this.threshold) {
                        text += cropName + " contributes " + Math.round(frac * 100) + "% to the " + clrName1 + " water footprint of " + basinName + ". ";
                    } else {
                        text += cropName + " is a small contributor (less than " + (this.threshold * 100) + "%) to the " + clrName1 + " water footprint of " + basinName + ". ";
                    }
                }
                text += "The pie chart on the right shows " + (frac < this.threshold? cropName.toLowerCase() + " compared to ": "") + "the sectors with the largest " + clrName1 + " water footprints in " + basinName + ". (\"Other\" contains all other sectors in the basin, of less than " + (this.threshold * 100) + "% each.) ";
                text += "Collective action should focus on the sectors in the pie chart. The potential water footprint gains for these sectors (insofar they concern crops) can be read from the bar chart below. The bar chart shows how their " + clrName0 + " water footprints in " + countryName + " compare to global " + clrName0 + " water footprint benchmarks.</p>";
            } else if (ws != null) {
                text += "<p class='font-weight-bold'><img src='/check.png' width='13' height='13'/> Sustainable</p>";
                text += "<p>The annual average " + clrName2 + " in " + basinName + " is " + getWSString(ws) + ". ";
                if (blue) {
                    text += "However, in some months it may be higher. The hydrograph on the left shows the monthly variety in the blue water scarcity. Whether the blue water footprint of " + cropName.toLowerCase() + " contributes to the monthly blue water scarcity in " + basinName + ", depends on the growing season of the " + cropName.toLowerCase() + ".";
                }

                text += "</p>";
            }
            return text;
        }
    },
    computed: {
        rows: function() {
            return this.countryBasinCrops.filter(val => {
                const viewColor = this.assessment.viewColor;
                const viewColor1 = viewColor.replace("blue", "greenBlue");
                return (val[viewColor + "WF"] > 0) && (val[viewColor1 + "WFt"] > 0) && (val[viewColor1 + "WFtSpot"] || val[viewColor + "WSSpot"]);
            });
        },
        attributeName: function() {
            const viewColor = this.assessment.viewColor;
            return { percent: `${viewColor}WFFraction`, value: `${viewColor}WF`};
        },
        columns: function() {
            const columns = [
                { key: "cropID", label: "Crop", dataFunction: function(val) { return getCrop(val).name; } },
                { key: "countryID", label: "Country", dataFunction: function(val) { return getCountry(val).name; } },
                { key: "basinID", label: "River Basin", dataFunction: function(val) { return !val? "(unknown basin)": getBasin(val).name; }}
            ];

            if (this.assessment.viewColor == "blue") {
                return columns.concat([
                    { key: "blueWF", label: "Blue WF (m3/yr)", dataFunction: getFunctionGetWFString("yr") },
                    { key: "blueWFFraction", label: "% of Blue WF in Total Selection", dataFunction: getFunctionGetPercentageString(1), classes: ["left-before-row"] },
                    { key: "greenBlueWFt", label: "Green-Blue WF (m3/t)", dataFunction: getWFtString, classes: ["group", "left-row"] },
                    { key: "greenBlueWFtExcess", label: "Deviation from Benchmark", dataFunction: getFunctionGetPercentageString(1), classes: "group" },
                    { key: "greenBlueWFtSpot", label: "Efficiency", dataFunction: getFunctionGetCheckImage("Efficient"), classes: ["group", "right-row"] },
                    { key: "whiteSpace", label: "", classes: ["white-space"] },
                    { key: "blueWS", label: "Blue Water Scarcity", dataFunction: getWSString, classes: ["group", "left-row"] },
                    { key: "blueWSSpot", label: "Sustainability", dataFunction: getFunctionGetCheckImage("Sustainable"), classes: ["group", "right-row"] },
                    { key: "blueWFFraction2", label: "% of Blue WF in River Basin by Crop", dataFunction: getFunctionGetPercentageString(this.threshold * 100), classes: ["right-after-row"] },
                    { key: "details", label: "Response Strategy" },
                ]);
            } else {  // grey
                return columns.concat([
                    { key: "greyWF", label: "Grey WF (m3/yr)", dataFunction: getFunctionGetWFString("yr") },
                    { key: "greyWFFraction", label: "% of Grey WF in Total Selection", dataFunction: getFunctionGetPercentageString(1), classes: ["left-before-row"] },
                    { key: "greyWFt", label: "Grey WF (m3/t)", dataFunction: getWFtString, classes: ["group", "left-row"] },
                    { key: "greyWFtExcess", label: "Deviation from Benchmark", dataFunction: getFunctionGetPercentageString(1), classes: "group" },
                    { key: "greyWFtSpot", label: "Efficiency", dataFunction: getFunctionGetCheckImage("Efficient"), classes: ["group", "right-row"] },
                    { key: "whiteSpace", label: "", classes: ["white-space"] },
                    { key: "greyWS", label: "Water Pollution Level", dataFunction: getWSString, classes: ["group", "left-row"] },
                    { key: "greyWSSpot", label: "Sustainability", dataFunction: getFunctionGetCheckImage("Sustainable"), classes: ["group", "right-row"] },
                    { key: "greyWFFraction2", label: "% of Grey WF in River Basin by Crop", dataFunction: getFunctionGetPercentageString(this.threshold * 100), classes: ["right-after-row"] },
                    { key: "details", label: "Response Strategy" }
                ]);
            }
        },
        pieData: function() {
            const filteredCrops = _.filter(this.basinCrops, (o) => (o.cropID == this.crop.cropID) || (o[this.attributeName.percent] >= this.threshold));
            const otherCrops = _.filter(this.basinCrops, (o) => (o.cropID != this.crop.cropID) && (o[this.attributeName.percent] < this.threshold));
            const data = _.map(filteredCrops, e => _.pick(e, ['cropName', this.attributeName.percent, this.attributeName.value]));
            const other = { cropName: "Other" };
            other[this.attributeName.percent] = _.sumBy(otherCrops, this.attributeName.percent);
            other[this.attributeName.value] = Math.round(_.sumBy(otherCrops, this.attributeName.value));
            data.push(other);

            return data;
        },
        barData: function () {
            const filteredBasinCrops = _.filter(this.basinCrops, (o) => (o.cropID == this.crop.cropID) || (o[this.attributeName.percent] >= this.threshold));
            const filteredCountryCrops = _.intersectionWith(this.countryCrops, filteredBasinCrops, (f, s) => f.cropID === s.cropID);
            let data = _.map(filteredCountryCrops, countryCrop => {
                const c = {
                    cropName: countryCrop.cropName,
                    wftExcess: countryCrop[this.assessment.viewColor.replace("blue", "greenBlue") + "WFtExcess"]
                };
                c.percent = Math.round(c.wftExcess * 100);
                c.tooltip = `${c.cropName}<br/>${Math.abs(c.percent)}%<br/>${c.wftExcess >= 0? "above ": "below"}<br/>benchmark`;
                return c;
            });
            data = _.filter(data, i => i.wftExcess != null);

            return data;
        },
        hydroData() {
            const data = {}
            const blueAvailabilities = _.map(this.basin.blueAvailabilities.split(','), e => parseFloat(e, 10) * Math.pow(10, 6));
            const blueFootprints = _.map(this.basin.blueFootprints.split(','), e => parseFloat(e, 10) * Math.pow(10, 6));
            data["lines"] = {
                blueAvailabilities: {data: blueAvailabilities, color: 'blue', stroke: 2, label: "Blue Water Availability"},
                blueFootprints: {data: blueFootprints, color: 'black', stroke: 2, label: "Blue Water Footprint"},
            };
            data["margin"] = { top: 10, right: 10, bottom: 220, left: 10 };
            data["areas"] = [
                {top: 'blueAvailabilities', bottom: 'blueFootprints', color: 'red'},
            ];
            data["columns"] = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

            return data;
        }
    }
}
</script>
<style lang="scss" scoped>
    .detail-header {
        font-weight: 700;
        font-size: 17px;
    }
</style>
